import { useCallback, useEffect, useState, useRef } from "react";
import {
  GfFigmaTemplateData,
  GfFlyerItemComponentPosition,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../../utils/api";
import { toastError } from "../../../../utils/pageUtil/toast";

export interface UseEditTemplateModalParams {
  open: boolean;
  item?: GfFlyerItemComponentPosition;
  onClose: () => void;
  onUpdated?: () => void;
}

export interface AutocompleteOption<T> {
  label: string;
  value: T;
}

export function useEditTemplateModal({
  open,
  item,
  onClose,
  onUpdated,
}: UseEditTemplateModalParams) {
  const [options, setOptions] = useState<
    AutocompleteOption<GfFigmaTemplateData>[]
  >([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<GfFigmaTemplateData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTemplateSelect = useCallback((values?: GfFigmaTemplateData[]) => {
    const template = values?.[0] || null;
    setSelectedTemplate(template);
  }, []);

  const mapToOption = useCallback(
    (d: GfFigmaTemplateData): AutocompleteOption<GfFigmaTemplateData> => ({
      label: d?.name || d?.id || "Unnamed",
      value: d,
    }),
    []
  );

  const fetchTemplates = useCallback(
    async (q: string) => {
      try {
        setError(null);
        setLoading(true);
        const data =
          await adminApiService.gfFigmaTemplateDataAdminControllerSearch(
            q,
            1,
            20
          );
        const items = data?.results || [];
        setOptions(items.map(mapToOption));
      } catch (err: any) {
        console.error("Failed to search figma template data", err);
        setError("Failed to load templates. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [mapToOption]
  );

  useEffect(() => {
    if (open) {
      if (item?.figmaTemplateData) {
        setSelectedTemplate(item.figmaTemplateData);
      } else {
        setSelectedTemplate(null);
      }
      fetchTemplates("");
    }
  }, [open, item]);

  const handleSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        fetchTemplates(value || "");
      }, 300);
    },
    [fetchTemplates]
  );

  const handleSave = useCallback(async () => {
    if (!item?.id) return;
    if (!selectedTemplate) {
      toastError("Select a template");
      return;
    }
    try {
      setSaving(true);
      await adminApiService.updateOneBaseGfFlyerItemComponentPositionsAdminControllerGfFlyerItemComponentPosition(
        {
          figmaTemplateData: { id: selectedTemplate.id } as GfFigmaTemplateData,
        },
        item.id
      );
      onUpdated?.();
      onClose();
    } catch (err) {
      console.error("Failed to update component position", err);
      toastError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [item?.id, selectedTemplate, onUpdated, onClose]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    options,
    selectedTemplate,
    handleTemplateSelect,
    loading,
    saving,
    error,
    handleSearch,
    handleSave,
  } as const;
}
